import React, { createContext, useContext, useState, useEffect } from 'react';

import {
	ACTION_ROLE,
	ACTION_TYPE,
	CMIC_FIELD_REST__NAMES,
	CMIC_KEYS,
	CMIC_TREE_NODE_TYPES,
	PAGE_LIMIT,
} from '../shared/Constants';
import { CmicContext } from './CmicContext';
import { fetchData } from '../utility/fetchData';
import { checkParentAddittion, handleBreadCrumbsAddittion, handleLoginUserViaAuth2 } from '../shared/UtilityFunctions';
import axios from 'axios';
import { MessageBarType } from '@fluentui/react';
import { defaultSortModel, handleOrderBy, handleSortFormat } from '../utility/grid/helper/SortHelper';
import { analytics } from '../utility/Analytics';
import _ from 'lodash';

//INITIALS

const INIT_ACTION_DATA = {
	nodeType: null,
	label: null,
	type: null,
};

const newPMObjectToastMsg = (msg, urlData = { name: '', id: '-', url: '' }, failedAttachmentNames, isSaveDraftFlag) => {
	const names = failedAttachmentNames ? `Attachments failed to upload:- ${failedAttachmentNames}` : '';
	const isNew = isSaveDraftFlag ? 'saved as a draft' : 'created';
	if (msg && urlData.url) {
		return (
			<div>
				New {urlData.name}{' '}
				<a href={urlData.url} target="_blank" rel="noreferrer">
					#{urlData.id}
				</a>{' '}
				has been {isNew}.{msg}.
				<br />
				{names}
			</div>
		);
	} else if (!msg && urlData.url) {
		return (
			<div>
				New {urlData.name}{' '}
				<a href={urlData.url} target="_blank" rel="noreferrer">
					#{urlData.id}
				</a>{' '}
				has been {isNew}.
				<br />
				{names}
			</div>
		);
	} else if (!msg && !urlData.url) {
		return (
			<div>
				New {urlData.name} #{urlData.id} has been {isNew}.
				<br />
				{names}
				<br />
				Unable to prepare deep link at the moment.
			</div>
		);
	} else return msg;
};

export const ActionContext = createContext({
	nodeData: [],
	attachmentData: [],
	actionTypeData: INIT_ACTION_DATA,
	breadcrumbs: {},
	activeNode: null,
	selectedEmailContent: {},
	selectedAttachmentsIds: {},
	pageInfo: {},
	docDataForUpload: {},
	sortModel: {},
	selectedImagingDoc: { value: '', label: '' },
	isSubmit: Boolean,
	isLoading: Boolean,
	autoFillMailToggle: Boolean,
	authProvider: {},
	handleBreadCrumbClicked: () => { },
	handleDataFetch: () => { },
	resetActionData: () => { },
	setActionTypeData: () => { },
	setIsSubmit: () => { },
	setAuthProvider: () => { },
	setItemFromEmail: () => { },
	setFieldsToCreateNewPMItems: () => { },
	setAttachmentData: () => { },
	setSelectedEmailContent: () => { },
	setSelectedAttachmentsIds: () => { },
	setAutoFillMailToggle: () => { },
	setSelectedImagingDoc: () => { },
	setSortModel: () => { },
	setActiveNode: () => { },
	setPageInfo: () => { },
	fetchDataPagination: () => { },
	fetchImagingDocsType: () => { },
	handleSwitchToBrowse: () => { },
	handleParentCountUpdate: () => { },
	handleImagingBackPress: () => { },
});

const isNotProjectType = type => {
	switch (type) {
		case CMIC_TREE_NODE_TYPES.ENTERPRISE_DOCUMENTS_PARENT:
			return true;

		default:
			return false;
	}
};

const ActionCtxt = ({ children }) => {
	const { userInfo, selectedProject, setShowToast } = useContext(CmicContext);

	const [actionTypeData, setActionTypeData] = useState(INIT_ACTION_DATA);
	const [nodes, setNodes] = useState({});
	const [breadcrumbs, setBreadcrumbs] = useState([]);
	const [activeNode, setActiveNode] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [fieldsToCreateNewPMItems, setFieldsToCreateNewPMItems] = useState({});
	const [itemFromEmail, setItemFromEmail] = useState({});
	const [pageInfo, setPageInfo] = useState({});
	const [selectedAttachmentsIds, setSelectedAttachmentsIds] = useState({});
	const [docDataForUpload, setDocDataForUpload] = useState({});
	const [attachmentData, setAttachmentData] = useState([]);
	const [selectedImagingDoc, setSelectedImagingDoc] = useState({ value: '', label: '' });
	const [selectedEmailContent, setSelectedEmailContent] = useState({
		blob: '',
		name: '',
		isAttached: false,
		isMailContent: true,
	});
	const [sortModel, setSortModel] = useState({});

	const [isSubmit, setIsSubmit] = useState(false);
	const [autoFillMailToggle, setAutoFillMailToggle] = useState(true);
	const [authProvider, setAuthProvider] = useState({});

	useEffect(() => {
		if (
			!actionTypeData.nodeType ||
			!actionTypeData.type ||
			actionTypeData.stopReload ||
			actionTypeData.type === ACTION_TYPE.IMAGING
		)
			return;

		resetActionData();
		if (isNotProjectType(actionTypeData.nodeType)) {
			handleDataFetch(actionTypeData.nodeType, { name: actionTypeData.label, data: {} }, true);
		} else {
			handleDataFetch(actionTypeData.nodeType, { ...selectedProject, name: actionTypeData.label }, true);
		}
	}, [actionTypeData, selectedProject.value]);

	useEffect(() => {
		if (!selectedImagingDoc?.value) return;

		resetActionData();
		handleDataFetch(actionTypeData.nodeType, { ...selectedImagingDoc, name: actionTypeData.label }, true);
	}, [selectedImagingDoc?.value]);

	const resetActionData = () => {
		setBreadcrumbs([]);
		setNodes({});
		setPageInfo({});
		setActiveNode(null);
		// setIsLoading(false);
	};

	const fetchImagingDocsType = async ({ searchOption = '', offset = 0 }) => {
		const cachedKey = localStorage.getItem(CMIC_KEYS.SELCTED_IMAGING_DOC_ENCRYPTION_KEY);
		const cachedSource = cachedKey ? JSON.parse(cachedKey) : '';
		if (cachedSource && !searchOption && !offset) {
			try {
				let URL = `${userInfo.serverURL}${CMIC_FIELD_REST__NAMES.ImgSources}?q=ImgsVUuid='${cachedSource?.ImgsVUuid}'`;
				const result = await axios.get(URL, {
					...handleLoginUserViaAuth2(userInfo),
				});
				if (result?.data?.count) {
					setSelectedImagingDoc(cachedSource);
				}
			} catch (err) {
				console.log(err);
			}
		}
		return new Promise(async (resolve, reject) => {
			try {
				const encodedQuery = encodeURIComponent(` LIKE UPPER('%${searchOption}%')`);
				const URL = `${userInfo.serverURL}${CMIC_FIELD_REST__NAMES.ImgSources
					}?onlyData=true&totalResults=false&limit=${searchOption ? 1000 : PAGE_LIMIT}&offset=${PAGE_LIMIT * offset
					}&orderBy=ImgsSourceDesc,ImgsVUuid&q=ImgsSourceDesc${encodedQuery}`;

				const result = await axios.get(URL, {
					headers: { 'REST-Framework-Version': '2' },
					...handleLoginUserViaAuth2(userInfo),
				});
				const requiredData = [];

				(result.data.items || []).forEach(item => {
					// Removing inactive imaging sources
					if (item?.ImgsActiveFlag === 'N' || item?.ImgsAllSourcesFlag === 'Y') return;
					requiredData.push({
						value: item.ImgsVUuid,
						label: item.ImgsSourceDesc,
						...item,
					});
				});

				if (!cachedSource) {
					//Analytics call for default selection of Imaging source from LOV.
					if (userInfo?.isCloudUser) {
						let obj = {};
						obj = {
							contactCode: userInfo?.info?.contactCode,
							timestamp: new Date(),
							imagingSourceVUuid: requiredData[0]?.ImgsVUuid,
							imagingSourceName: requiredData[0]?.ImgsSourceDesc,
							actionType: 'Default Selection',
						};
						analytics.track({ event: 'Imaging Source Selected' }, obj);
					}
					setSelectedImagingDoc(requiredData[0]);
				}
				resolve({
					mainData: {
						items: requiredData,
						hasMore: result.data?.hasMore,
						limit: result.data?.limit,
						offset: result.data?.offset + 1,
						count: result.data?.count,
					},
				});
				return;
			} catch (error) {
				console.log('fetchImagingDocsType Error=', error);
				resolve([]);
				setShowToast({
					isVisible: true,
					msg: `Failed to fetch Imaging sources.`,
					type: MessageBarType.error,
				});
			}
		});
	};

	const fetchDataPagination = async (nodeType, config) => {
		let result = {};
		nodeType = nodeType || activeNode;
		try {
			setIsLoading(true);
			const extraReqData = userInfo.actionRole === ACTION_ROLE.PROJECT ? selectedProject : selectedImagingDoc;
			if (pageInfo[nodeType]) {
				result = await fetchData({ ...pageInfo[nodeType], ...config }, nodeType, {
					...userInfo,
					...extraReqData,
				});
				setPageInfo(prev => ({
					...prev,
					[nodeType]: {
						...prev[nodeType],
						...config,
					},
				}));
			} else {
				result = await fetchData({ ...config }, nodeType, { ...userInfo, ...extraReqData });
				setPageInfo(prev => ({
					...prev,
					[nodeType]: {
						...config,
					},
				}));
			}

			if (!result || !result.mainData) {
				setNodes(prev => ({
					...prev,
					[nodeType]: {
						...prev[nodeType],
						items: [],
					},
				}));
			} else {
				setNodes(prev => ({
					...prev,
					[nodeType]: result?.mainData,
				}));
			}
			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			console.log('fetchDataPagination() Error', error);
		}
	};

	const handleParentCountUpdate = (uploadCount, callBack, activeNodeIndex) => {
		if (!uploadCount) {
			setShowToast({
				isVisible: true,
				msg: `Attachment(s) failed to upload.`,
				type: MessageBarType.error,
			});
			return setIsLoading(false);
		}

		const bCrumbsLength = breadcrumbs.length - 1;
		const parentNode = bCrumbsLength <= 0 ? breadcrumbs[0].id : breadcrumbs[bCrumbsLength - 1].id;

		if (!parentNode) return setIsLoading(false);

		try {
			const requiredNode = { ...nodes[parentNode] };

			requiredNode.items = requiredNode.items.map(item => {
				if (item.id === docDataForUpload[activeNode]?.id) {
					if (activeNode === CMIC_TREE_NODE_TYPES.ENTERPRISE_DOCUMENT_REVISION && !item.count) {
						uploadCount++;
					}
					return {
						...item,
						count: item.count + uploadCount,
					};
				} else {
					return item;
				}
			});

			setDocDataForUpload(prev => ({
				...prev,
				[activeNode]: {
					...prev[activeNode],
					count: prev[activeNode].count + uploadCount,
				},
			}));

			setShowToast({
				isVisible: true,
				msg: `${uploadCount} attachments added`,
				type: MessageBarType.success,
			});

			const clonedNode = _.cloneDeep(requiredNode);

			// When the active node is Enterprise Document Type, we need to update count in parent node's items array
			if (activeNode === CMIC_TREE_NODE_TYPES.ENTERPRISE_DOCUMENT_TYPE) {
				const nodeFromParent = nodes[CMIC_TREE_NODE_TYPES.ENTERPRISE_DOCUMENTS_PARENT].items[activeNodeIndex];
				nodeFromParent.count = nodeFromParent.count + uploadCount;

				setNodes(prev => ({
					...prev,
					[CMIC_TREE_NODE_TYPES.ENTERPRISE_DOCUMENTS_PARENT]: {
						...prev[CMIC_TREE_NODE_TYPES.ENTERPRISE_DOCUMENTS_PARENT],
						count: prev[CMIC_TREE_NODE_TYPES.ENTERPRISE_DOCUMENTS_PARENT].count + uploadCount,
					},
				}));
			} else {
				setNodes(prev => ({
					...prev,
					[parentNode]: requiredNode,
				}));
			}
			setIsLoading(false);

			callBack();
		} catch (error) {
			callBack();
			console.log('handleParentCountUpdate() Error', error);
		}
	};
	// @desc-> This function will come into play one when we create New RFI/Issue/Communication
	const handleSwitchToBrowse = async (data, nodeType, url, failedAttachmentNames, isSaveDraftFlag) => {
		//Initiation of states to update
		const nodes = {};
		const pageInfo = {};
		const breadCrumbs = [];
		let activeNode = nodeType;
		const orderBy = handleOrderBy(activeNode);
		let result = null;
		setIsLoading(true);

		try {
			// Need to get result in sorted order to view results on top

			result = await fetchData({ name: data.name, orderBy }, activeNode, {
				...userInfo,
				...selectedProject,
			});

			// Adding Parent Node
			nodes[activeNode] = result?.mainData;
			breadCrumbs.push({
				id: activeNode,
				label: data.name,
			});
			pageInfo[activeNode] = {
				num: 0,
				nodeId: data.nodeId,
				orderBy: null,
				filter: '',
			};

			//Finding newly Created PM Item

			const requiredItem = (result?.mainData?.items || []).find(e => e.nodeId === data.nodeId);

			if (requiredItem) {
				activeNode = requiredItem.ecmParentNodeType;
				result = await fetchData({ ...requiredItem }, activeNode, { ...userInfo, ...selectedProject });

				nodes[activeNode] = result?.mainData;
				breadCrumbs.push({
					id: activeNode,
					label: requiredItem.name,
				});
				pageInfo[activeNode] = {
					num: 0,
					nodeId: requiredItem.nodeId,
					orderBy: null,
					filter: '',
					VUuid: requiredItem.VUuid,
				};

				setActionTypeData(prev => ({
					...prev,
					type: ACTION_TYPE.EXISTING,
					stopReload: true,
				}));
				setNodes(nodes);
				setActiveNode(activeNode);
				setBreadcrumbs(breadCrumbs);
				setPageInfo(pageInfo);
				setDocDataForUpload(prev => ({
					...prev,
					[activeNode]: requiredItem,
				}));
				setIsLoading(false);

				setShowToast({
					isVisible: true,
					msg: newPMObjectToastMsg(
						'',
						{
							name: data.name,
							id: data.uuid,
							url,
						},
						failedAttachmentNames,
						isSaveDraftFlag,
					),
					type: MessageBarType.success,
					isPersistent: true,
				});
			} else {
				setShowToast({
					isVisible: true,
					msg: newPMObjectToastMsg(
						`Unable to view ${data?.name || '-'}`,
						{
							name: data.name,
							id: data.uuid,
							url,
						},
						failedAttachmentNames,
					),
					type: MessageBarType.success,
					isPersistent: true,
				});

				handleDataFetch(actionTypeData.nodeType, { ...selectedProject, name: actionTypeData.label }, true);
			}
		} catch (error) {
			console.log('handleSwitchToBrowse() ERROR:', error);
			setIsLoading(false);
		}
	};

	const handleDataFetch = async (nodeType, data, isFresh) => {
		if (nodeType === CMIC_TREE_NODE_TYPES.PROJECT_DOCUMENT) return;
		setIsLoading(true);

		// Used to set previous nodes data required for API calling in child node
		if (
			data.ecmParentNodeType === CMIC_TREE_NODE_TYPES.PROJECT_RFI ||
			data.ecmParentNodeType === CMIC_TREE_NODE_TYPES.PROJECT_ISSUE ||
			data.ecmParentNodeType === CMIC_TREE_NODE_TYPES.PROJECT_DAILY_JOURNAL ||
			data.ecmParentNodeType === CMIC_TREE_NODE_TYPES.PROJECT_SUBMITTALS ||
			data.ecmParentNodeType === CMIC_TREE_NODE_TYPES.PROJECT_PCI ||
			data.ecmParentNodeType === CMIC_TREE_NODE_TYPES.PROJECT_DOCUMENT_TYPE ||
			data.ecmParentNodeType === CMIC_TREE_NODE_TYPES.PROJECT_DOCUMENT_TYPE_REVISION ||
			data.ecmParentNodeType === CMIC_TREE_NODE_TYPES.ENTERPRISE_DOCUMENT_TYPE ||
			data.ecmParentNodeType === CMIC_TREE_NODE_TYPES.ENTERPRISE_DOCUMENT_REVISION
		) {
			setDocDataForUpload(prev => ({
				...prev,
				[nodeType]: data,
			}));
		} else {
			setDocDataForUpload({});
		}

		const sortOrder = sortModel[nodeType] || defaultSortModel(nodeType);
		const orderBy = handleSortFormat(sortOrder, nodeType);
		let result = null;
		const requiredData = data?.data || data;
		const extraReqData = userInfo?.actionRole === ACTION_ROLE.PROJECT ? selectedProject : selectedImagingDoc;
		try {
			result = await fetchData({ ...requiredData, orderBy }, nodeType, {
				...userInfo,
				...extraReqData,
			});

			let resultingData = [];

			resultingData = result?.mainData || null;

			setPageInfo(prev => ({
				...prev,
				[nodeType]: {
					num: 0,
					nodeId: data.nodeId,
					orderBy,
					filter: '',
					VUuid: data.VUuid,
					PmdgtTypeCode: data?.PmdgtTypeCode || '',
					documentTypeName: data?.ImgdDocumentTypeDesc || '',
					documentName: data?.ImgdDocumentName || '',
				},
			}));

			setSortModel(prev => ({
				...prev,
				[nodeType]: [...sortOrder],
			}));

			// NEED TO CHECK whether we need revisions in here or not
			if (checkParentAddittion(nodeType)) {
				setNodes(prev => ({
					...prev,
					[nodeType]: resultingData,
				}));
			} else {
				setNodes(prev => ({
					...prev,
					[nodeType]: resultingData,
				}));
			}

			setActiveNode(nodeType);

			const brdCrmbs = handleBreadCrumbsAddittion(
				nodeType,
				data?.name || data?.data?.name || '-',
				isFresh ? [] : breadcrumbs,
			);

			setBreadcrumbs(brdCrmbs);

			setIsLoading(false);
		} catch (error) {
			console.log('handleDataFetch() Error=', error);
			setIsLoading(false);
			setActiveNode(nodeType);
		}
	};

	const handleBreadCrumbClicked = data => {
		if (data?.id === activeNode) return;
		// Creating copy to avoid unecessary data loosing
		const breadCrumbs = [...breadcrumbs];

		// Finding index to do actual task
		const requiredIndex = breadCrumbs.findIndex(e => e?.id === data?.id);

		// Stopping if we didn't found required node OR User Clicked last node
		if (requiredIndex === -1 || requiredIndex === breadCrumbs.length - 1) return;

		// Filtering data
		const remainingCrumbs = breadCrumbs.splice(requiredIndex + 1);

		filterNodeDataWithCrumbs(remainingCrumbs);
		setBreadcrumbs(breadCrumbs);
		setActiveNode(data?.id);
	};

	const handleImagingBackPress = nodeType => {
		filterNodeDataWithCrumbs(breadcrumbs);
		setBreadcrumbs([]);
		setImmediate(() => {
			setActiveNode(nodeType);
		});
	};

	const filterNodeDataWithCrumbs = (crumbs = []) => {
		// Removing extra data from nodes
		const nodeData = { ...nodes };
		const pageInfoData = { ...pageInfo };

		(crumbs || []).forEach(item => {
			if (nodeData[item?.id]) delete nodeData[item?.id];
			if (pageInfoData[item?.id]) delete pageInfoData[item?.id];
		});
		setNodes(nodeData);
		setPageInfo(pageInfoData);
	};

	return (
		<ActionContext.Provider
			value={{
				nodeData: nodes[activeNode],
				actionTypeData,
				breadcrumbs,
				activeNode,
				fieldsToCreateNewPMItems,
				itemFromEmail,
				docDataForUpload,
				isLoading,
				isSubmit,
				attachmentData,
				selectedEmailContent,
				selectedAttachmentsIds,
				autoFillMailToggle,
				selectedImagingDoc,
				pageInfo,
				sortModel,
				authProvider,
				setActiveNode,
				setSelectedImagingDoc,
				setActionTypeData,
				setIsSubmit,
				setAuthProvider,
				setIsLoading,
				setFieldsToCreateNewPMItems,
				setItemFromEmail,
				setAttachmentData,
				setSelectedEmailContent,
				setSelectedAttachmentsIds,
				setAutoFillMailToggle,
				setSortModel,
				handleBreadCrumbClicked,
				handleDataFetch,
				resetActionData,
				fetchDataPagination,
				fetchImagingDocsType,
				handleSwitchToBrowse,
				handleParentCountUpdate,
				handleImagingBackPress,
			}}
		>
			{children}
		</ActionContext.Provider>
	);
};

export default ActionCtxt;
